import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Switch,
  Stack,
  Radio,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import { configService } from '@/services/configService'
import { toast } from 'react-toastify'

const Configuration = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [tab, setTab] = useState(0)
  const [channels, setChannels] = useState<any[]>([])
  const [statuses, setStatuses] = useState<any[]>([])
  const [depositConfigs, setDepositConfigs] = useState<any[]>([])
  const [withdrawConfigs, setWithdrawConfigs] = useState<any[]>([])
  const [channelForm, setChannelForm] = useState({ name: '', description: '' })
  const [statusForm, setStatusForm] = useState({ name: '', description: '' })
  const [editingDepositId, setEditingDepositId] = useState<string | null>(null)
  const [editingDepositValues, setEditingDepositValues] = useState<{ minAmount: number; maxAmount: number } | null>(null)

  const refreshData = async () => {
    const [ch, st, dep, wd] = await Promise.all([
      configService.listChannels(),
      configService.listStatuses(),
      configService.listDepositConfigs(),
      configService.listWithdrawalConfigs(),
    ])
    setChannels(ch)
    setStatuses(st)
    setDepositConfigs(dep)
    setWithdrawConfigs(wd)
  }

  useEffect(() => {
    refreshData()
  }, [])

  const handleAddChannel = async () => {
    try {
      const created = await configService.addChannel(channelForm)
      setChannels((prev) => [...prev, created])
      setChannelForm({ name: '', description: '' })
      toast.success('Channel added')
    } catch (error: any) {
      toast.error(error.message || 'Failed to add channel')
    }
  }

  const handleAddStatus = async () => {
    try {
      const created = await configService.addStatus(statusForm)
      setStatuses((prev) => [...prev, created])
      setStatusForm({ name: '', description: '' })
      toast.success('Status added')
    } catch (error: any) {
      toast.error(error.message || 'Failed to add status')
    }
  }

  const handleDepositToggle = async (id: string, key: 'isActive' | 'isDefault' | 'canReceivePaymentNotification', value: boolean) => {
    console.log('handleDepositToggle called:', { id, key, value })
    try {
      const payload = { [key]: value }
      console.log('Sending PATCH request to:', `/config/deposit-channel-configurations/${id}`, 'with payload:', payload)
      await configService.updateDepositConfig(id, payload)
      console.log('Update successful, refreshing list...')
      const updated = await configService.listDepositConfigs()
      setDepositConfigs(updated)
      toast.success('Configuration updated successfully')
    } catch (error: any) {
      console.error('Error updating deposit config:', error)
      toast.error(error.message || 'Failed to update configuration')
    }
  }

  const handleWithdrawToggle = async (id: string, key: 'isActive' | 'isDefault', value: boolean) => {
    try {
      await configService.updateWithdrawalConfig(id, { [key]: value })
      const updated = await configService.listWithdrawalConfigs()
      setWithdrawConfigs(updated)
      toast.success('Configuration updated successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update configuration')
    }
  }

  const handleDepositDefaultChange = async (id: string) => {
    await handleDepositToggle(id, 'isDefault', true)
  }

  const handleWithdrawDefaultChange = async (id: string) => {
    await handleWithdrawToggle(id, 'isDefault', true)
  }

  const handleStartEditDeposit = (config: any) => {
    setEditingDepositId(config.id)
    setEditingDepositValues({
      minAmount: config.minAmount,
      maxAmount: config.maxAmount,
    })
  }

  const handleSaveDepositEdit = async (id: string) => {
    if (!editingDepositValues) return
    
    try {
      await configService.updateDepositConfig(id, {
        minAmount: editingDepositValues.minAmount,
        maxAmount: editingDepositValues.maxAmount,
      })
      const updated = await configService.listDepositConfigs()
      setDepositConfigs(updated)
      setEditingDepositId(null)
      setEditingDepositValues(null)
      toast.success('Configuration updated successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update configuration')
    }
  }

  const handleCancelDepositEdit = () => {
    setEditingDepositId(null)
    setEditingDepositValues(null)
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Configuration & Administration
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_e, value) => setTab(value)} variant="scrollable">
          <Tab label="Channels" />
          <Tab label="Statuses" />
          <Tab label="Deposit Configurations" />
          <Tab label="Withdrawal Configurations" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6">Add Channel</Typography>
              <Stack spacing={2} mt={2}>
                <TextField
                  label="Name"
                  value={channelForm.name}
                  onChange={(e) => setChannelForm((prev) => ({ ...prev, name: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={channelForm.description}
                  onChange={(e) => setChannelForm((prev) => ({ ...prev, description: e.target.value }))}
                  fullWidth
                />
                <Button variant="contained" onClick={handleAddChannel} disabled={!channelForm.name}>
                  Add Channel
                </Button>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Channels List
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {channels.map((channel) => (
                      <TableRow key={channel.id}>
                        <TableCell>{channel.name}</TableCell>
                        <TableCell>{channel.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6">Add Status</Typography>
              <Stack spacing={2} mt={2}>
                <TextField
                  label="Name"
                  value={statusForm.name}
                  onChange={(e) => setStatusForm((prev) => ({ ...prev, name: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={statusForm.description}
                  onChange={(e) => setStatusForm((prev) => ({ ...prev, description: e.target.value }))}
                  fullWidth
                />
                <Button variant="contained" onClick={handleAddStatus} disabled={!statusForm.name}>
                  Add Status
                </Button>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Statuses List
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statuses.map((status) => (
                      <TableRow key={status.id}>
                        <TableCell>{status.name}</TableCell>
                        <TableCell>{status.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Deposit Channel Configurations
          </Typography>
            <TableContainer 
              sx={{ 
                maxWidth: '100%',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                  },
                },
              }}
            >
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Paybill</TableCell>
                    <TableCell>Min Amount</TableCell>
                    <TableCell>Max Amount</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Default</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Payment Notifications</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {depositConfigs.map((cfg) => (
                  <TableRow key={cfg.id}>
                    <TableCell>{cfg.paybill}</TableCell>
                    <TableCell>
                      {editingDepositId === cfg.id ? (
                        <TextField
                          type="number"
                          size="small"
                          value={editingDepositValues?.minAmount || ''}
                          onChange={(e) =>
                            setEditingDepositValues((prev) =>
                              prev ? { ...prev, minAmount: parseFloat(e.target.value) || 0 } : null
                            )
                          }
                          inputProps={{ step: '0.01', min: '0' }}
                          sx={{ width: { xs: '100%', sm: '120px' }, minWidth: { xs: '80px', sm: '120px' } }}
                        />
                      ) : (
                        cfg.minAmount
                      )}
                    </TableCell>
                    <TableCell>
                      {editingDepositId === cfg.id ? (
                        <TextField
                          type="number"
                          size="small"
                          value={editingDepositValues?.maxAmount || ''}
                          onChange={(e) =>
                            setEditingDepositValues((prev) =>
                              prev ? { ...prev, maxAmount: parseFloat(e.target.value) || 0 } : null
                            )
                          }
                          inputProps={{ step: '0.01', min: '0' }}
                          sx={{ width: { xs: '100%', sm: '120px' }, minWidth: { xs: '80px', sm: '120px' } }}
                        />
                      ) : (
                        cfg.maxAmount
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={cfg.isActive}
                        onChange={(e) => handleDepositToggle(cfg.id, 'isActive', e.target.checked)}
                        disabled={editingDepositId === cfg.id}
                      />
                    </TableCell>
                    <TableCell>
                      <Radio
                        checked={cfg.isDefault}
                        onChange={() => handleDepositDefaultChange(cfg.id)}
                        size="small"
                        disabled={editingDepositId === cfg.id}
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Switch
                        checked={cfg.canReceivePaymentNotification ?? false}
                        onChange={(e) => handleDepositToggle(cfg.id, 'canReceivePaymentNotification', e.target.checked)}
                        disabled={editingDepositId === cfg.id}
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {isMobile ? new Date(cfg.updatedAt).toLocaleDateString() : new Date(cfg.updatedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {editingDepositId === cfg.id ? (
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSaveDepositEdit(cfg.id)}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={handleCancelDepositEdit}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleStartEditDeposit(cfg)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Withdrawal Channel Configurations
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Paybill</TableCell>
                  <TableCell>Initiator</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {withdrawConfigs.map((cfg) => (
                  <TableRow key={cfg.id}>
                    <TableCell>{cfg.paybill}</TableCell>
                    <TableCell>{cfg.initiatorName}</TableCell>
                    <TableCell>
                      <Switch
                        checked={cfg.isActive}
                        onChange={(e) => handleWithdrawToggle(cfg.id, 'isActive', e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Radio
                        checked={cfg.isDefault}
                        onChange={() => handleWithdrawDefaultChange(cfg.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(cfg.updatedAt).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  )
}

export default Configuration


